import { useCallback, useEffect, useRef, useState } from 'react';
import { JobRequestHandler } from '@/lib/http/JobRequestHandler';
import type { QueueResponse } from '@/lib/http/ApiClient';
import { useApiClient } from '@/hooks/useApiClient';
import { useTranslation } from '@/hooks/useTranslation';
import {
  MISSING_SIGNAL_DATA_ERROR_NAME,
  MISSING_SIGNAL_SOURCE_ERROR_NAME,
  NO_MACHINING_PASS_ERROR_NAME,
} from '@/lib/http/errors';
import { mlSubmitInference, mlGetInferenceStatus } from '@/features/machine-learning/api/MLApiClient';
import type {
  MLInferenceRequest,
  MLInferenceResult,
  MLSignalsWindow,
} from '@/features/machine-learning/type/MLTypes';
import type { TranslationParams } from '@/context/LanguageContext';

type InferenceStatus = 'idle' | 'running' | 'completed' | 'failed';

type MLJobQueueResult = QueueResponse & {
  result?: MLInferenceResult;
  message?: string;
};

type MLDirectInferenceResult = {
  jobId: string;
  status?: 'completed';
  result?: MLInferenceResult;
  message?: string;
};

/**
 * The text shown for the live-signals errors the user can act on; null for other errors (their
 * server message is shown as is). Shared by the prediction and the signal chart.
 */
export function signalErrorMessage(
  err: unknown,
  t: (key: string, params?: TranslationParams) => string
): string | null {
  if (!(err instanceof Error)) return null;
  if (err.name === MISSING_SIGNAL_SOURCE_ERROR_NAME) return t('mlRul.errors.missingSignalSource');
  if (err.name === MISSING_SIGNAL_DATA_ERROR_NAME) return t('mlRul.errors.noSignalData');
  if (err.name === NO_MACHINING_PASS_ERROR_NAME) {
    // "No machining pass found in the last 300 s of part 10281.003 on <machine>. …"
    const match = /last (\d+) s of part (\S+) on /.exec(err.message);
    return match
      ? t('mlRul.errors.noMachiningPass', { seconds: match[1], part: match[2] })
      : t('mlRul.errors.noMachiningPassGeneric');
  }
  return null;
}

export function useMLInferenceJob(): {
  status: InferenceStatus;
  jobId: string | null;
  rulSeconds: number | null;
  warning: string | null;
  signalsWindow: MLSignalsWindow | null;
  error: string | null;
  run: (req: MLInferenceRequest) => void;
  cancel: () => void;
} {
  const [status, setStatus] = useState<InferenceStatus>('idle');
  const [jobId, setJobId] = useState<string | null>(null);
  const [rulSeconds, setRulSeconds] = useState<number | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const [signalsWindow, setSignalsWindow] = useState<MLSignalsWindow | null>(null);
  const [error, setError] = useState<string | null>(null);
  const http = useApiClient();
  const { t } = useTranslation();

  const handlerRef = useRef<JobRequestHandler<MLJobQueueResult> | null>(null);

  const cancel = useCallback(() => {
    handlerRef.current?.cancel();
    handlerRef.current = null;
    setStatus('idle');
    setWarning(null);
    setSignalsWindow(null);
  }, []);

  const run = useCallback(
    (req: MLInferenceRequest) => {
      handlerRef.current?.cancel();

      setStatus('running');
      setJobId(null);
      setRulSeconds(null);
      setWarning(null);
      setSignalsWindow(null);
      setError(null);

      const handler = new JobRequestHandler<MLJobQueueResult>({
        initFn: async () => {
          const raw = (await mlSubmitInference(req, http ?? undefined)) as MLDirectInferenceResult;
          if (raw.status === 'completed') {
            return {
              ok: true,
              data: {
                jobId: raw.jobId,
                action: 'undefined' as const,
                status: 'completed' as const,
                timestamp: new Date().toISOString(),
                ...(raw.result !== undefined && { result: raw.result }),
                ...(raw.message !== undefined && { message: raw.message }),
              },
            };
          }
          return raw as unknown as import('@/lib/http/HttpClient').ResponseError & { jobId?: string };
        },
        statusFn: async (id: string) => {
          const raw = await mlGetInferenceStatus(id);
          return {
            ok: true as const,
            data: {
              jobId: raw.jobId,
              action: 'undefined' as const,
              status: raw.status,
              timestamp: raw.timestamp,
              ...(raw.result !== undefined && { result: raw.result }),
              ...(raw.message !== undefined && { message: raw.message }),
            },
          };
        },
        setResult: (result) => {
          if (!result) return;

          if (result.ok) {
            const data = result.data;
            setJobId(data.jobId);

            if (data.status === 'completed') {
              setRulSeconds(data.result?.rulSeconds ?? null);
              setWarning(data.result?.warning ?? null);
              setSignalsWindow(data.result?.window ?? null);
              setStatus('completed');
            } else if (data.status === 'failed') {
              setError(data.message ?? 'errors.generic');
              setStatus('failed');
            }
          } else {
            if ('jobId' in result && result.jobId) {
              setJobId(result.jobId as string);
            }
            setError(result.message ?? 'errors.generic');
            setStatus('failed');
          }
        },
      });

      handlerRef.current = handler;
      handler.start().catch((err) => {
        setError(
          signalErrorMessage(err, t) ??
            (err instanceof Error && err.message ? err.message : 'errors.generic')
        );
        setStatus('failed');
      });
    },
    [http, t]
  );

  useEffect(() => {
    return () => {
      handlerRef.current?.cancel();
    };
  }, []);

  return { status, jobId, rulSeconds, warning, signalsWindow, error, run, cancel };
}
